import { Check, LayoutList, Plus } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { useMode } from "@/hooks/useMode";
import { useLanguage } from "@/hooks/useLanguage";
import { useOrganization } from "@/hooks/useOrganization";
import { usePlanGate } from "@/hooks/usePlanGate";
import { useCurrentUser } from "@/hooks/useCurrentUser";
import { ChevronsUpDownIcon } from "@/components/icons/NavIcons";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { SidebarMenu, SidebarMenuButton, SidebarMenuItem } from "@/components/ui/sidebar";

const copy = {
  cs: {
    businessFinances: "Firemní finance",
    personalFinances: "Osobní finance",
    personal: "Osobní",
    joinOrCreate: "Připojit se nebo vytvořit organizaci",
    allClients: "Přehled všech firem",
    switchWorkspace: "Přepnout organizaci nebo režim",
  },
  en: {
    businessFinances: "Business finances",
    personalFinances: "Personal finances",
    personal: "Personal",
    joinOrCreate: "Join or create organization",
    allClients: "All companies overview",
    switchWorkspace: "Switch organization or mode",
  },
};

/**
 * Top-of-sidebar switcher: the organization AND Business/Private mode in one
 * control (it replaced the separate organization and mode switchers of the old top bar).
 * Personal finances is listed as its own workspace next to the organizations.
 * Text only — no organization logo or monogram; hidden on the collapsed rail.
 */
export function WorkspaceSwitcher() {
  const { mode, setMode } = useMode();
  const { language, t } = useLanguage();
  const navigate = useNavigate();
  const { currentOrganization, organizations, setCurrentOrganization } = useOrganization();
  const { data: user } = useCurrentUser();
  const { blockedByFeature } = usePlanGate();
  const s = copy[language] || copy.cs;

  const year = new Date().getFullYear();
  // Switching mode lands on that mode's overview for the current year.
  const enterBusiness = () => {
    if (mode === "business") return;
    setMode("business");
    navigate(`/${language}/dashboard?year=${year}`);
  };
  const enterPrivate = () => {
    if (mode === "private") return;
    // Private mode is part of Firma: the person's best plan over the companies they own.
    if (blockedByFeature("privateMode")) return;
    setMode("private");
    navigate(`/${language}/personal/overview?year=${year}`);
  };

  const orgName = currentOrganization?.name?.trim() || t.myCompany || "Moje organizace";
  const isPrivate = mode === "private";
  const userLabel = user?.email ?? s.personal;

  return (
    <SidebarMenu className="group-data-[collapsible=icon]:hidden">
      <SidebarMenuItem>
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <SidebarMenuButton
              size="lg"
              aria-label={s.switchWorkspace}
              className="h-12 gap-2 rounded-lg border border-sidebar-border bg-sidebar-accent/40 px-3 hover:bg-sidebar-accent data-[state=open]:bg-sidebar-accent"
            >
              <span className="grid min-w-0 flex-1 leading-tight">
                <span className="truncate text-sm font-semibold">
                  {isPrivate ? s.personalFinances : orgName}
                </span>
                <span className="truncate text-xs text-muted-foreground">
                  {isPrivate ? userLabel : s.businessFinances}
                </span>
              </span>
              <ChevronsUpDownIcon className="ml-auto size-4 text-muted-foreground" />
            </SidebarMenuButton>
          </DropdownMenuTrigger>
          <DropdownMenuContent
            className="w-[--radix-dropdown-menu-trigger-width] min-w-56"
            side="bottom"
            align="start"
            sideOffset={4}
          >
            {organizations.length > 0 && (
              <>
                <DropdownMenuLabel className="text-xs font-medium text-muted-foreground">
                  {t.switchOrganization || "Switch Organization"}
                </DropdownMenuLabel>
                {organizations.map((org) => {
                  const roleLabel =
                    org.role === "owner"
                      ? t.roleOwner
                      : org.role === "admin"
                        ? t.roleAdmin
                        : t.roleMember;
                  const name = org.name || t.myCompany || "My organization";
                  return (
                    <DropdownMenuItem
                      key={org.id}
                      onClick={() => {
                        setCurrentOrganization(org);
                        enterBusiness();
                      }}
                      className="cursor-pointer gap-2.5"
                    >
                      <span className="flex min-w-0 flex-1 flex-col">
                        <span className="truncate text-sm font-medium">{name}</span>
                        <span className="text-xs text-muted-foreground">{roleLabel}</span>
                      </span>
                      {!isPrivate && currentOrganization?.id === org.id && (
                        <Check className="h-4 w-4 text-primary" />
                      )}
                    </DropdownMenuItem>
                  );
                })}
                <DropdownMenuSeparator />
              </>
            )}
            <DropdownMenuItem onClick={enterPrivate} className="cursor-pointer gap-2.5">
              <span className="flex min-w-0 flex-1 flex-col">
                <span className="truncate text-sm font-medium">{s.personalFinances}</span>
                <span className="truncate text-xs text-muted-foreground">{userLabel}</span>
              </span>
              {isPrivate && <Check className="h-4 w-4 text-[hsl(var(--personal-primary))]" />}
            </DropdownMenuItem>
            <DropdownMenuSeparator />
            {organizations.length > 1 && (
              <DropdownMenuItem
                onClick={() => {
                  if (mode !== "business") setMode("business");
                  navigate(`/${language}/business/clients`);
                }}
                className="cursor-pointer"
              >
                <LayoutList className="mr-2 h-4 w-4 text-muted-foreground" />
                {s.allClients}
              </DropdownMenuItem>
            )}
            <DropdownMenuItem
              onClick={() => navigate(`/${language}/settings/organization`)}
              className="cursor-pointer text-muted-foreground"
            >
              <Plus className="mr-2 h-4 w-4" />
              {s.joinOrCreate}
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </SidebarMenuItem>
    </SidebarMenu>
  );
}
